/**
 * Brand config — the single white-label surface for the storefront.
 * Carrier defaults; overridable interactively or via `carrier site create --brand`.
 */
export interface Brand {
  name: string;
  legalName: string;
  tagline: string;
  /** One sentence for <meta name="description"> and social cards. */
  description: string;
  domain: string;
  supportEmail: string;
  supportUrl: string;
  supportWhatsapp: string;
  /** Display name on transactional mail. */
  emailFromName: string;
  /** Mailbox transactional mail is sent from. The address is this plus `domain`. */
  emailFromMailbox: string;
  /** Public origin of the storefront, no trailing slash. */
  appUrl: string;
  colors: {
    /** Page background (deepest ink). */
    bg: string;
    /** Primary accent. */
    accent: string;
    /** Accent pressed/dark. */
    accentDark: string;
    /** Primary text. */
    text: string;
  };
  social: {
    x?: string;
    linkedin?: string;
    instagram?: string;
    tiktok?: string;
  };
  /** MCP control-plane endpoint the storefront's API talks to. */
  carrierApiUrl: string;
}

export const CARRIER_BRAND: Brand = {
  name: "Carrier",
  legalName: "Lifecycle Innovations Limited",
  tagline: "Programmable connectivity, on demand.",
  description: "Data that works in 200+ countries. Instant QR activation. Human support.",
  domain: "carrier.llc",
  supportEmail: "support@carrier.llc",
  supportUrl: "https://carrier.llc/help",
  supportWhatsapp: "+17864604829",
  emailFromName: "Carrier",
  emailFromMailbox: "hello",
  appUrl: "https://carrier.llc",
  colors: {
    bg: "#080C16",
    accent: "#FF6B35",
    accentDark: "#D9461C",
    text: "#F5F1EA",
  },
  social: {
    x: "@carrier_llc",
    instagram: "@carrier.llc",
    tiktok: "@carrier.llc",
  },
  carrierApiUrl: "https://api.carrier.llc",
};

const CARRIER_ACCENT_LIGHT = "#FFB088";
const CARRIER_ACCENT_GRADIENT_START = "#FF7A45";

function hexToRgb(hex: string): [number, number, number] | null {
  const h = hex.replace(/^#/, "");
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHex(r: number, g: number, b: number, lower = false): string {
  const fmt = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, "0");
  const out = `#${fmt(r)}${fmt(g)}${fmt(b)}`;
  return lower ? out : out.toUpperCase();
}

function mixHexWithWhite(hex: string, whiteRatio: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const mix = (n: number) => n + (255 - n) * whiteRatio;
  return rgbToHex(mix(rgb[0]), mix(rgb[1]), mix(rgb[2]));
}

function darkenHex(hex: string, factor: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const scale = (n: number) => n * factor;
  return rgbToHex(scale(rgb[0]), scale(rgb[1]), scale(rgb[2]), true);
}

/** Derive a darker pressed-state accent; keeps the Carrier default pair when accent is unchanged. */
export function deriveAccentDark(accent: string, seed: Brand = CARRIER_BRAND): string {
  if (accent.toUpperCase() === seed.colors.accent.toUpperCase()) return seed.colors.accentDark;
  const rgb = hexToRgb(accent);
  if (!rgb) return accent;
  const scale = (n: number) => Math.max(0, Math.min(255, Math.round(n * 0.75)));
  return rgbToHex(scale(rgb[0]), scale(rgb[1]), scale(rgb[2]));
}

/** Derive a lighter accent for glows and `--brand-accent-light`. */
export function deriveAccentLight(accent: string, seed: Brand = CARRIER_BRAND): string {
  if (accent.toUpperCase() === seed.colors.accent.toUpperCase()) return CARRIER_ACCENT_LIGHT;
  return mixHexWithWhite(accent, 0.45);
}

/** Derive the lighter hero-gradient stop paired with accent. */
export function deriveAccentGradientStart(accent: string, seed: Brand = CARRIER_BRAND): string {
  if (accent.toUpperCase() === seed.colors.accent.toUpperCase()) return CARRIER_ACCENT_GRADIENT_START;
  return mixHexWithWhite(accent, 0.08);
}

/** Tree-wide hex swaps for accent-derived template colors (gradients, the accent scale, CSS vars). */
export function deriveAccentPaletteSubs(accent: string, accentDark: string): Array<[string, string]> {
  if (accent.toUpperCase() === CARRIER_BRAND.colors.accent.toUpperCase()) return [];
  const accentLight = deriveAccentLight(accent);
  const gradientStart = deriveAccentGradientStart(accent);
  return [
    ["#FF6B35", accent],
    ["#D9461C", accentDark],
    ["#FFB088", accentLight],
    ["#FF7A45", gradientStart],
    ["#fff4ef", mixHexWithWhite(accent, 0.94).toLowerCase()],
    ["#ffe0d0", mixHexWithWhite(accent, 0.85).toLowerCase()],
    ["#ffbfa0", mixHexWithWhite(accent, 0.7).toLowerCase()],
    ["#ff9970", mixHexWithWhite(accent, 0.55).toLowerCase()],
    ["#ff7d4d", mixHexWithWhite(accent, 0.4).toLowerCase()],
    ["#b33a17", darkenHex(accentDark, 0.75)],
    ["#8a2d12", darkenHex(accentDark, 0.58)],
    ["#5e1e0c", darkenHex(accentDark, 0.4)],
    ["#3a1107", darkenHex(accentDark, 0.25)],
  ];
}

/** Serialize a brand to the `src/brand.config.ts` the storefront imports. */
export function renderBrandConfig(brand: Brand): string {
  return `// Auto-generated by @carrierllc/mcp — edit freely to re-brand this storefront.
// This is the single white-label surface: name, copy, colors, domain, support.
export interface Brand {
  name: string;
  legalName: string;
  tagline: string;
  description: string;
  domain: string;
  supportEmail: string;
  supportUrl: string;
  emailFromName: string;
  emailFromMailbox: string;
  appUrl: string;
  colors: { bg: string; accent: string; accentDark: string; text: string };
  social: { x?: string; linkedin?: string };
  carrierApiUrl: string;
}

export const brand: Brand = ${JSON.stringify(brand, null, 2)};

export default brand;
`;
}

/** Serialize a brand to a `.env.local` for the storefront. */
export function renderEnv(brand: Brand): string {
  return [
    `# Generated by @carrierllc/mcp`,
    `NEXT_PUBLIC_BRAND_NAME=${JSON.stringify(brand.name)}`,
    `NEXT_PUBLIC_CARRIER_API_URL=${JSON.stringify(brand.carrierApiUrl)}`,
    `# Public site origin. Used to build absolute URLs for guest checkout.`,
    `# \`carrier site deploy\` writes the deployed URL back here.`,
    `NEXT_PUBLIC_APP_URL=`,
    `# Required whenever NEXT_PUBLIC_CARRIER_API_URL points at a live origin:`,
    `# the catalog client throws without it, so / and /shop return 500.`,
    `# \`carrier site deploy\` fills this in and pushes it to the deploy target.`,
    `CARRIER_API_KEY=`,
    `# Guest checkout calls Stripe server-side. Without this, checkout fails at`,
    `# request time while every other page keeps working.`,
    `STRIPE_SECRET_KEY=`,
    `# Optional. With a publishable key the payment form is embedded in this`,
    `# site's own checkout page; without one the customer is sent to Stripe's`,
    `# hosted page and back. Both take money — the embedded one loses fewer`,
    `# people on the way.`,
    `NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY=`,
    `# Optional. Sends the eSIM QR code by email once an order is ready.`,
    `# \`carrier site email\` authenticates you at Resend in the browser and`,
    `# fills this in. Without it the storefront still sells and activates; the`,
    `# activation page just says email delivery is not set up.`,
    `# The from-address is composed from brand.config.ts (emailFromMailbox +`,
    `# domain), so verify that domain at resend.com/domains first.`,
    `RESEND_API_KEY=`,
    `# Clerk — \`carrier site clerk\` fills these in, or paste them from dashboard.clerk.com`,
    `NEXT_PUBLIC_CLERK_PUBLISHABLE_KEY=`,
    `CLERK_SECRET_KEY=`,
    ``,
  ].join("\n");
}
